/* eslint-disable camelcase */

/**
 * The default type that is being provided by the Newsnet External Services
 * API. (Without the hashtags thing.)
 */
import browserCheck from '@ta-interaktiv/browsercheck';

/**
 * We use different share URLs in the native app, so these links can be
 * captured by the app and redirected to the native share extensions.
 * @type {{facebook: string, twitter: string, whatsapp: string, share: string}}
 * @private
 */
var _NATIVE_APP_SHARE_URLS = {
  facebook: 'app://share_facebook',
  twitter: 'app://share_twitter',
  whatsapp: 'app://share_whatsapp',
  share: 'app://share_email'
  /**
   * The condition to use the native share URL versions.
   * @private
   */

};
var _NATIVE_SHARE_URL_CONDITION = browserCheck.isNativeApp;
/**
 * Create a URL to share an object on twitter.
 *
 * @param {Object} parameters
 * @param {string} parameters.url URL to share
 * @param {string=} parameters.title Description or title to be prefilled as
 * text.
 * @param {string=} parameters.via The Twitter handle that should be
 * prefilled as «via».
 * @param {Array.string=} parameters.hashtags The hashtags that the tweet
 * should prefill.
 * @returns {string} The action URL that opens the Twitter share screen.
 * @throws {TypeError} if url is missing.
 */

function twitterShareUrl(parameters) {
  // Shortcut: In native apps use the native version
  if (_NATIVE_SHARE_URL_CONDITION) {
    return _NATIVE_APP_SHARE_URLS['twitter'];
  }

  var title = parameters.title,
      url = parameters.url,
      via = parameters.via,
      hashtags = parameters.hashtags;

  if (url === undefined) {
    throw new TypeError('[Share URL Factory] URL not defined');
  } // Basic URL


  var shareUrl = 'https://twitter.com/intent/tweet'; // Add URL
  // We assume that there is always a URL present.
  // This might bite us in our posteriors at some future time.

  shareUrl += "?url=".concat(encodeURIComponent(url)); // Add title or additional text

  if (title) {
    shareUrl += "&text=".concat(encodeURIComponent(title));
  } // Add via


  if (via) {
    shareUrl += "&via=".concat(encodeURIComponent(via));
  } // Add hashtags


  if (hashtags) {
    shareUrl += "&hashtags=".concat(encodeURIComponent(hashtags.join(',')));
  }

  return shareUrl;
}
/**
 * Create an URL to share an object on Facebook.
 *
 * @param {Object} parameters
 * @param {string} parameters.url The URL to be shared.
 * @param {number} parameters.facebook_id The Facebook App ID.
 * @param {Array.string=} parameters.hashtags The hashtags to share the
 * content with. Similar to Twitter, but here only the first object is
 * taken, so place your hashtags wisely!
 * @returns {string} The URL with which the Facebook share dialog is being
 * opened.
 * @throws {TypeError} if url or facebook_id is undefined.
 */


function facebookShareUrl(parameters) {
  // Shortcut for native apps
  if (_NATIVE_SHARE_URL_CONDITION) {
    return _NATIVE_APP_SHARE_URLS['facebook'];
  }

  var url = parameters.url,
      hashtags = parameters.hashtags,
      facebook_id = parameters.facebook_id;

  if (url === undefined) {
    throw new TypeError('[Share URL Factory] URL not defined');
  }

  if (facebook_id === undefined) {
    throw new TypeError('[Share URL Factory] Facebook ID not defined.');
  }

  var shareUrl = 'https://www.facebook.com/dialog/share';
  shareUrl += '?display=popup';
  shareUrl += "&app_id=".concat(facebook_id); // Basic URL

  shareUrl += "&href=".concat(encodeURIComponent(url));
  shareUrl += "&redirect_uri=".concat(encodeURIComponent(url)); // Add hashtags

  if (hashtags) {
    shareUrl += "&hashtag=".concat(encodeURIComponent("#".concat(hashtags[0])));
  }

  return shareUrl;
}
/**
 * Create a URL to share an object on WhatsApp.
 *
 * @param {Object} parameters
 * @param {string} parameters.url URL to share
 * @param {string=} parameters.title Description or title to be prefilled as
 * text.
 * @returns {string} The action URL that opens WhatsApp.
 * @throws {TypeError} if url is missing.
 */


function whatsappShareUrl(parameters) {
  // Shortcut for native apps
  if (_NATIVE_SHARE_URL_CONDITION) {
    return _NATIVE_APP_SHARE_URLS['whatsapp'];
  }

  var title = parameters.title,
      url = parameters.url;

  if (url === undefined) {
    throw new TypeError('[Share URL Factory] URL not defined');
  } // Basic URL


  var shareUrl = 'whatsapp://send'; // Add URL

  shareUrl += "?text=".concat(encodeURIComponent(url)); // Add title or additional text

  if (title) {
    shareUrl += "".concat(encodeURIComponent(' ')).concat(encodeURIComponent(title));
  }

  return shareUrl;
}
/**
 * Create an URL to send a URL as e-mail.
 * @param {Object} parameters
 * @param {string} parameters.url In this case the actual Share URL.
 * @return {string} The share URL.
 */


function shareShareUrl(parameters) {
  // shortcut for native apps
  if (_NATIVE_SHARE_URL_CONDITION) {
    return _NATIVE_APP_SHARE_URLS['share'];
  }

  var url = parameters.url;

  if (url === undefined) {
    throw new TypeError('[Share URL Factory] Share URL not defined');
  }

  return encodeURI(url);
}
/**
 * The actual factory. The dictionary keys correspond to the share object type
 * property, so it's easy to call the correct function for the specific share
 * object
 * @see {@link shareObject}
 * @type {{twitter: twitterShareUrl, facebook: facebookShareUrl, whatsapp:
 *   whatsappShareUrl, share: shareShareUrl}}
 */


export var shareURLFactory = {
  twitter: twitterShareUrl,
  facebook: facebookShareUrl,
  whatsapp: whatsappShareUrl,
  share: shareShareUrl
};
export default shareURLFactory;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@ta-interaktiv/share-url-factory/es/shareURLFactory.js
// module id = null
// module chunks = 